/**
 * Japanese notes and coins of the drawer count (T07-26, design/05 §3.7).
 *
 * Counting is `count × face value` per row and their sum — the number of yen in the drawer the
 * cashier has in hand, not a price, a tax or a sales total (the task allows it). The server
 * checks the rows again (`denominations[]`) and is the one that compares with what should be in
 * the drawer: the tablet never knows the expected amount before the close.
 */

export const NOTES = [10000, 5000, 2000, 1000] as const;
export const COINS = [500, 100, 50, 10, 5, 1] as const;
export const ALL_DENOMINATIONS = [...NOTES, ...COINS] as const;
export type DenominationValue = (typeof ALL_DENOMINATIONS)[number];

export type Counts = Partial<Record<DenominationValue, number>>;

export const MAX_COUNT = 9999;

/** The yen of one row (face value × pieces). */
export function rowTotal(value: DenominationValue, count: number | undefined): number {
  return value * (count ?? 0);
}

/** The whole count (Σ rows). */
export function countTotal(counts: Counts): number {
  return ALL_DENOMINATIONS.reduce((sum, value) => sum + rowTotal(value, counts[value]), 0);
}

/** The rows sent to pos-service (only the ones with pieces, largest first). */
export function toPayload(counts: Counts): Array<{ value: DenominationValue; count: number }> {
  return ALL_DENOMINATIONS.filter((value) => (counts[value] ?? 0) > 0).map((value) => ({
    value,
    count: counts[value] ?? 0,
  }));
}

export function clampCount(count: number): number {
  if (!Number.isFinite(count) || count < 0) {
    return 0;
  }
  return Math.min(Math.trunc(count), MAX_COUNT);
}

/** Rows the server kept (report.cash.denominations) → counts. */
export function fromPayload(rows: unknown): Counts {
  const counts: Counts = {};
  if (!Array.isArray(rows)) {
    return counts;
  }
  for (const row of rows) {
    if (typeof row !== "object" || row === null) {
      continue;
    }
    const { value, count } = row as { value?: unknown; count?: unknown };
    if (
      typeof value === "number" &&
      typeof count === "number" &&
      (ALL_DENOMINATIONS as readonly number[]).includes(value)
    ) {
      counts[value as DenominationValue] = count;
    }
  }
  return counts;
}
